import {
  createSlice,
  createSelector,
  type PayloadAction,
} from "@reduxjs/toolkit";
import {
  BasicPersonInfo,
  CaseType,
  CreateCaseFormContent,
  EmailAddressInfo,
  Gender,
  IdentityInfo,
  PhoneNumberInfo,
  initialMemberInfo,
} from "components/ekyc/case/utils";
import { AddressInfo } from "components/ekyc/case/utils";

interface CreateCaseState {
  lastPathname: string;
  form: CreateCaseFormContent;
}

const initialState: CreateCaseState = {
  lastPathname: "",
  form: {
    type: "INDIVIDUAL",
    members: [],
  },
};

export const createCaseSlice = createSlice({
  name: "createCase",
  initialState,
  reducers: {
    resetData: (state) => {
      state.form = initialState.form;
    },
    setLastPathname: (state, action: PayloadAction<string>) => {
      const pathname = action.payload;
      state.lastPathname = pathname;
    },
    setFormCaseReference: (state, action: PayloadAction<string>) => {
      const caseReference = action.payload;
      state.form.caseReference = caseReference;
    },
    setFormCaseType: (state, action: PayloadAction<CaseType>) => {
      const type = action.payload;
      state.form.type = type;
    },
    setPersonInfoField: (
      state,
      action: PayloadAction<{
        field: keyof BasicPersonInfo;
        value: BasicPersonInfo[keyof BasicPersonInfo];
        idx: number;
      }>
    ) => {
      const { field, value, idx } = action.payload;
      if (!state.form.members[idx]) {
        state.form.members[idx] = {
          ...initialMemberInfo,
          [field]: value,
        };
      } else {
        switch (field) {
          case "gender":
            if (!!value) {
              state.form.members[idx][field] = value as Gender;
            } else {
              delete state.form.members[idx][field];
            }
            break;
          default:
            state.form.members[idx][field] = value as string;
            break;
        }
      }
    },
    setFormIdentities: (
      state,
      action: PayloadAction<{ idx: number; identities: IdentityInfo[] }>
    ) => {
      const { idx, identities } = action.payload;
      if (!state.form.members[idx]) {
        state.form.members[idx] = {
          ...initialMemberInfo,
          identities: identities,
        };
      } else {
        state.form.members[idx].identities = identities;
      }
    },
    setFormPhoneNumbers: (
      state,
      action: PayloadAction<{ idx: number; phoneNumbers: PhoneNumberInfo[] }>
    ) => {
      const { idx, phoneNumbers } = action.payload;
      if (!state.form.members[idx]) {
        state.form.members[idx] = {
          ...initialMemberInfo,
          phoneNumbers: phoneNumbers,
        };
      } else {
        state.form.members[idx].phoneNumbers = phoneNumbers;
      }
    },
    setFormEmailAddresses: (
      state,
      action: PayloadAction<{ idx: number; emailAddresses: EmailAddressInfo[] }>
    ) => {
      const { idx, emailAddresses } = action.payload;
      if (!state.form.members[idx]) {
        state.form.members[idx] = {
          ...initialMemberInfo,
          emailAddresses,
        };
      } else {
        state.form.members[idx].emailAddresses = emailAddresses;
      }
    },
    setFormAddresses: (
      state,
      action: PayloadAction<{ idx: number; addresses: AddressInfo[] }>
    ) => {
      const { idx, addresses } = action.payload;
      if (!state.form.members[idx]) {
        state.form.members[idx] = {
          ...initialMemberInfo,
          addresses: addresses,
        };
      } else {
        state.form.members[idx].addresses = addresses;
      }
    },
    deletePersonalInfoData: (state, action: PayloadAction<number>) => {
      const idx = action.payload;
      if (state.form.members.length > 1) {
        state.form.members = state.form.members.filter(
          (_, index) => index !== idx
        );
      } else {
        state.form.members = [
          {
            ...initialMemberInfo,
          },
        ];
      }
    },
  },
});

const selectCreateCase = (state: { createCase: CreateCaseState }) =>
  state.createCase;

export const selectCreateCaseState = createSelector(
  selectCreateCase,
  (createCase: CreateCaseState) => ({
    lastPathname: createCase.lastPathname,
    form: createCase.form,
  })
);

export const {
  resetData,
  setLastPathname,
  setFormCaseReference,
  setFormCaseType,
  setPersonInfoField,
  setFormIdentities,
  setFormPhoneNumbers,
  setFormEmailAddresses,
  setFormAddresses,
  deletePersonalInfoData,
} = createCaseSlice.actions;

export default createCaseSlice.reducer;
